import { Routes } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { onboardingCheckGuard } from '@core/guards/onboarding-check.guard';
import { onboardingGuard } from '@pages/onboarding/onboardingGuard';
import { getStartedRedirectGuard } from '@pages/onboarding/get-started-redirect.guard';
import { landingRedirectGuard } from '@pages/landing/landing-redirect.guard';
import { adminGuard } from './guards/admin.guard';

export const appRoutes: Routes = [
  {
    path: '',
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'landing'
      },
      {
        path: 'landing',
        pathMatch: 'full',
        canActivate: [landingRedirectGuard],
        loadComponent: () => import('./pages/landing/landing-page').then(m => m.LandingPageComponent)
      },
      { path: 'about', loadComponent: () => import('./pages/about/about-page').then(m => m.AboutPageComponent) },
      { path: 'privacy', loadComponent: () => import('./pages/privacy-policy/privacy-policy-page').then(m => m.PrivacyPolicyPageComponent) },
      { path: 'cookie-policy', loadComponent: () => import('./pages/cookie-policy/cookie-policy-page').then(m => m.CookiePolicyPageComponent) },
      { path: 'terms', loadComponent: () => import('./pages/terms-of-service/terms-of-service-page').then(m => m.TermsOfServicePageComponent) },
      { path: 'medical-disclaimer', loadComponent: () => import('./pages/medical-disclaimer/medical-disclaimer-page').then(m => m.MedicalDisclaimerPageComponent) },
      {
        path: 'community-guidelines',
        loadComponent: () => import('./pages/community-guidelines/community-guidelines-page').then(m => m.CommunityGuidelinesPageComponent)
      },
      { path: 'contact', loadComponent: () => import('./pages/contact/contact-page').then(m => m.ContactPageComponent) },
      {
        path: 'support',
        loadComponent: () => import('./pages/contact/contact-page').then(m => m.ContactPageComponent),
        data: { tone: 'support' }
      },
      {
        path: 'support/moderation',
        loadComponent: () => import('./pages/contact/contact-page').then(m => m.ContactPageComponent),
        data: { tone: 'moderation', topic: 'moderation', lockTopic: true }
      },
      {
        path: 'appeal/:actionId',
        loadComponent: () => import('./pages/appeal/appeal-page').then(m => m.AppealPageComponent)
      },

      {
        path: 'legal/terms-of-service',
        loadComponent: () => import('./pages/terms-of-service/terms-of-service-page').then(m => m.TermsOfServicePageComponent)
      },
      {
        path: 'legal/community-guidelines',
        loadComponent: () => import('./pages/community-guidelines/community-guidelines-page').then(m => m.CommunityGuidelinesPageComponent)
      },

      {
        path: 'accept-terms',
        canActivate: [MsalGuard],
        loadComponent: () => import('./pages/accept-terms/accept-terms-page').then(m => m.AcceptTermsPageComponent)
      },

      {
        path: 'home',
        canActivate: [MsalGuard, onboardingCheckGuard],
        loadComponent: () => import('./pages/home/home-page').then(m => m.HomePageComponent)
      },
      {
        path: 'feed',
        canActivate: [MsalGuard, onboardingCheckGuard],
        loadComponent: () => import('./pages/your-feed/your-feed-page').then(m => m.YourFeedPage)
      },
      { path: 'categories', loadComponent: () => import('./pages/categories/categories-page').then(m => m.CategoriesPage) },
      { path: 'communities', redirectTo: 'categories', pathMatch: 'full' },
      {
        path: 'ask',
        canActivate: [MsalGuard, onboardingCheckGuard],
        loadComponent: () => import('./pages/ask-question/ask-question-page').then(m => m.AskQuestionPage)
      },
      {
        path: 'appointments',
        canActivate: [MsalGuard, onboardingCheckGuard],
        loadComponent: () =>
          import('./pages/appointments-list/appointments-list-page').then(m => m.AppointmentsListPage)
      },
      {
        path: 'appointments/new',
        canActivate: [MsalGuard, onboardingCheckGuard],
        loadComponent: () =>
          import('./pages/appointment-create/appointment-create-page').then(m => m.AppointmentCreatePage)
      },
      {
        path: 'appointments/:id/prep',
        canActivate: [MsalGuard, onboardingCheckGuard],
        loadComponent: () =>
          import('./pages/appointment-prep/appointment-prep-page').then(m => m.AppointmentPrepPage)
      },
      {
        path: 'appointments/:id/sheet',
        canActivate: [MsalGuard, onboardingCheckGuard],
        loadComponent: () =>
          import('./pages/appointment-sheet/appointment-sheet-page').then(m => m.AppointmentSheetPage)
      },
      {
        path: 'appointments/:id/visit',
        canActivate: [MsalGuard, onboardingCheckGuard],
        loadComponent: () =>
          import('./pages/appointment-post-visit/appointment-post-visit-page').then(m => m.AppointmentPostVisitPage)
      },
      {
        path: 'get-started',
        canActivate: [getStartedRedirectGuard],
        loadComponent: () => import('./pages/onboarding/get-started-page').then(m => m.GetStartedPage)
      },
      { path: 'login', loadComponent: () => import('./pages/login/login-page').then(m => m.LoginPage) },
      { path: 'auth/login', redirectTo: 'login', pathMatch: 'full' },
      {
        path: 'auth/verify',
        loadComponent: () => import('./pages/auth-link/auth-link-page').then(m => m.AuthLinkPageComponent),
        data: { kind: 'verify' }
      },
      {
        path: 'auth/reset-password',
        loadComponent: () => import('./pages/auth-link/auth-link-page').then(m => m.AuthLinkPageComponent),
        data: { kind: 'reset' }
      },
      { path: 'auth/callback', loadComponent: () => import('./pages/auth-callback/auth-callback-page').then(m => m.AuthCallbackPage) },
      {
        path: 'activity',
        canActivate: [MsalGuard, onboardingCheckGuard],
        loadComponent: () => import('./pages/activity/activity-feed-component/activity-feed-component').then(m => m.ActivityFeedComponent)
      },
      {
        path: 'admin/moderation',
        canActivate: [MsalGuard, onboardingCheckGuard, adminGuard],
        loadComponent: () =>
          import('./features/moderation/admin/moderation-queue/moderation-queue.component').then(
            m => m.ModerationQueueComponent
          ),
        title: 'Moderation Queue - PeerLiv Admin'
      },
      {
        path: 'admin/moderation/keywords',
        canActivate: [MsalGuard, onboardingCheckGuard, adminGuard],
        loadComponent: () =>
          import('./features/moderation/admin/keyword-management/keyword-management.component').then(
            m => m.KeywordManagementComponent
          ),
        title: 'Keyword Management - PeerLiv Admin'
      },
      {
        path: 'admin/moderation/users',
        canActivate: [MsalGuard, onboardingCheckGuard, adminGuard],
        loadComponent: () =>
          import('./features/moderation/admin/user-moderation/user-moderation.component').then(
            m => m.UserModerationComponent
          ),
        title: 'User Moderation - PeerLiv Admin'
      },
      {
        path: 'admin/moderation/decisions',
        canActivate: [MsalGuard, onboardingCheckGuard, adminGuard],
        loadComponent: () =>
          import('./features/moderation/admin/recent-decisions/recent-decisions.component').then(
            m => m.RecentDecisionsComponent
          ),
        title: 'Recent Automated Decisions - PeerLiv Admin'
      },
      {
        path: 'admin/ai-operations',
        canActivate: [MsalGuard, onboardingCheckGuard, adminGuard],
        loadComponent: () =>
          import('./admin/ai-operations/ai-operations-hub.component').then(
            m => m.AiOperationsHubComponent
          ),
        title: 'Azure AI Operations - PeerLiv Admin'
      },
      {
        path: 'admin/ai-traces',
        canActivate: [MsalGuard, onboardingCheckGuard, adminGuard],
        loadComponent: () =>
          import('./features/ai-diagnostics/ai-diagnostics-page.component').then(
            m => m.AiDiagnosticsPageComponent
          ),
        title: 'AI Diagnostics - PeerLiv Admin'
      },
      {
        path: 'admin/ai-review',
        canActivate: [MsalGuard, onboardingCheckGuard, adminGuard],
        loadComponent: () =>
          import('./features/admin/ai-review/pages/ai-review-page.component').then(
            m => m.AiReviewPageComponent
          ),
        title: 'AI Answer Review - PeerLiv Admin'
      },
      {
        path: 'admin/trusted-knowledge',
        canActivate: [MsalGuard, onboardingCheckGuard, adminGuard],
        loadComponent: () =>
          import('./features/admin/trusted-knowledge/pages/trusted-knowledge-page.component').then(
            m => m.TrustedKnowledgePageComponent
          ),
        title: 'Trusted Knowledge - PeerLiv Admin'
      },
      {
        path: 'admin/ai-evaluations',
        canActivate: [MsalGuard, onboardingCheckGuard, adminGuard],
        loadComponent: () =>
          import('./admin/ai-evaluations/ai-evaluation-workbench.component').then(
            m => m.AiEvaluationWorkbenchComponent
          ),
        title: 'AI Evaluation Workbench - PeerLiv Admin'
      },
      {
        path: 'admin/ai/drift',
        canActivate: [MsalGuard, onboardingCheckGuard, adminGuard],
        loadComponent: () =>
          import('./features/admin/ai-drift/pages/ai-drift-monitor-page.component').then(
            m => m.AiDriftMonitorPageComponent
          ),
        title: 'AI Drift Monitor - PeerLiv Admin'
      },
      

      {
        path: 'me/settings',
        redirectTo: 'me',
        pathMatch: 'full'
      },
      {
        path: 'me/notifications',
        canActivate: [MsalGuard, onboardingCheckGuard],
        loadComponent: () => import('./pages/me/me-page').then(m => m.MePageComponent),
        data: { panel: 'notifications' }
      },
      {
        path: 'me/settings/notifications',
        canActivate: [MsalGuard, onboardingCheckGuard],
        loadComponent: () => import('./pages/me/me-page').then(m => m.MePageComponent),
        data: { panel: 'notifications' }
      },
      {
        path: 'settings/notifications',
        canActivate: [MsalGuard, onboardingCheckGuard],
        loadComponent: () => import('./pages/me/me-page').then(m => m.MePageComponent),
        data: { panel: 'notifications' }
      },
      {
        path: 'me/settings/account',
        canActivate: [MsalGuard, onboardingCheckGuard],
        loadComponent: () => import('./pages/me/me-page').then(m => m.MePageComponent),
        data: { panel: 'account' }
      },
      {
        path: 'me/settings/blocked',
        canActivate: [MsalGuard, onboardingCheckGuard],
        loadComponent: () => import('./pages/me/me-page').then(m => m.MePageComponent),
        data: { panel: 'blocked' }
      },
      {
        path: 'me/settings/privacy',
        redirectTo: 'me',
        pathMatch: 'full'
      },
      {
        path: 'me/privacy',
        canActivate: [MsalGuard, onboardingCheckGuard],
        loadComponent: () => import('./pages/me/me-page').then(m => m.MePageComponent),
        data: { panel: 'privacy' }
      },
      {
        path: 'me/privacy/past-posts',
        canActivate: [MsalGuard, onboardingCheckGuard],
        loadComponent: () => import('./pages/me/me-page').then(m => m.MePageComponent),
        data: { panel: 'privacy' }
      },
      {
        path: 'me/posts',
        canActivate: [MsalGuard, onboardingCheckGuard],
        loadComponent: () => import('./pages/me/my-posts/my-posts').then(m => m.MyPostsPage)
      },
      {
        path: 'me/saved',
        canActivate: [MsalGuard, onboardingCheckGuard],
        loadComponent: () => import('./pages/me/saved-posts/saved-posts').then(m => m.SavedPostsPage)
      },

      {
        path: 'onboarding-required',
        loadComponent: () => import('./pages/onboarding/onboarding-required-page').then(m => m.OnboardingRequiredPage)
      },
      {
        path: 'onboarding',
        canActivate: [MsalGuard, onboardingGuard],
        loadComponent: () => import('./pages/onboarding/onboarding-page').then(m => m.OnboardingComponent)
      },
      {
        path: 'me',
        canActivate: [MsalGuard, onboardingCheckGuard],
        loadComponent: () => import('./pages/me/me-page').then(m => m.MePageComponent)
      },
      {
        path: 'category/:id',
        canActivate: [MsalGuard, onboardingCheckGuard],
        loadComponent: () => import('./pages/category-feed/category-feed-page').then(m => m.CategoryFeedComponent)
      },
      {
        path: 'community/:id',
        canActivate: [MsalGuard, onboardingCheckGuard],
        loadComponent: () => import('./pages/category-feed/category-feed-page').then(m => m.CategoryFeedComponent)
      },
      { path: 'me/profile', redirectTo: 'me', pathMatch: 'full' },
      {
        path: 'post/:id/reply',
        canActivate: [MsalGuard, onboardingCheckGuard],
        loadComponent: () => import('./pages/me/post-details/post-details').then(m => m.PostDetailComponent)
      },
      {
        path: 'post/:id',
        canActivate: [MsalGuard, onboardingCheckGuard],
        loadComponent: () => import('./pages/me/post-details/post-details').then(m => m.PostDetailComponent)
      },
      {
        path: 'posts/:id/reply',
        canActivate: [MsalGuard, onboardingCheckGuard],
        loadComponent: () => import('./pages/me/post-details/post-details').then(m => m.PostDetailComponent)
      },
      {
        path: 'posts/:id',
        canActivate: [MsalGuard, onboardingCheckGuard],
        loadComponent: () => import('./pages/me/post-details/post-details').then(m => m.PostDetailComponent)
      },
      {
        path: 'search',
        canActivate: [MsalGuard, onboardingCheckGuard],
        loadComponent: () => import('./pages/search/search-page').then(m => m.SearchPage)
      },
    ],
  },
];

